import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  IMembershipType,
  IProduct,
  IProductCode,
  useMembershipTypeOptionsQuery,
  useProductCodesLazyQuery,
  useUpdateProductGeneralInfoMutation
} from '../../../generated/graphql';
import EditButton from '../../Common/Buttons/EditButton';
import { AppContext } from '../../../AppContext';
import { canUserEdit } from '../../../utils/rightUtils';
import { Types } from '../../../enums/TypeEnums';
import { Colors } from '../../../utils/theme';
import { defaultErrorsObj, ErrorTexts, IErrors } from '../NewProduct';
import { Actions } from '../../../enums/ActionEnums';
import { sameProductNameExists } from '../../../utils/clientUtils';
import InputComponent from '../../Common/Fields/InputComponent';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import { RB_NAME } from '../../../enums/MembershipEnums';

interface IProps {
  product: IProduct;
}

const ProductGeneralTab: React.FunctionComponent<IProps> = ({ product }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const membershipTypes = useMembershipTypeOptionsQuery({ fetchPolicy: 'no-cache' });
  const [getProductCodes, productCodesResult] = useProductCodesLazyQuery();
  const [editable, setEditable] = useState(false);
  const [name, setName] = useState(product.name);
  const [productCode, setProductCode] = useState<IProductCode | null>(product.product_code);
  const [membershipType, setMembershipType] = useState(product.membership_type);
  const [price, setPrice] = useState(product.price);
  const [vat, setVat] = useState(product.vat);
  const [bottomNotes, setBottomNotes] = useState(product.bottom_notes || '');
  const [productCodesOptions, setProductCodesOptions] = useState([product.product_code]);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [updateProduct, { data, loading }] = useUpdateProductGeneralInfoMutation();
  const canEdit = canUserEdit(state.user, Types.Product);

  const handleEditClicked = () => {
    getProductCodes();
    setEditable(true);
  };

  useEffect(() => {
    if (productCodesResult.data && productCodesResult.data.result) {
      setProductCodesOptions(productCodesResult.data.result as never[]);
    }
  }, [productCodesResult.data]);

  useEffect(() => {
    if (data && data.result) {
      setEditable(false);
      dispatch({
        type: Actions.SetSelectedProduct,
        payload: {
          name: data.result.name
        }
      });
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          text: `Product ${data.result.name} updated`,
          severity: 'success'
        }
      });
    }
  }, [data]);

  const handleProductCodeChanged = (_: unknown, newValue: IProductCode | null) => {
    if (newValue) {
      setFormErrors({ ...formErrors, productCode: null });
    } else {
      setFormErrors({ ...formErrors, productCode: ErrorTexts.productCode });
    }
    setMembershipType(null);
    setProductCode(newValue);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        name: ErrorTexts.name
      });
    } else {
      setFormErrors({ ...formErrors, name: null });
    }
    setName(value);
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value && Number(value) >= 0) {
      setFormErrors({ ...formErrors, price: null });
    } else {
      setFormErrors({
        ...formErrors,
        price: ErrorTexts.price
      });
    }
    setPrice(Number(value));
  };

  const handleVatChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value && (Number(value) < 0 || Number(value) > 100)) {
      setFormErrors({
        ...formErrors,
        vatRange: ErrorTexts.vatRange
      });
    } else {
      setFormErrors({ ...formErrors, vatRange: null });
    }
    setVat(Number(value));
  };

  const handleCancel = () => {
    setName(product.name);
    setProductCode(product.product_code);
    setBottomNotes(product.bottom_notes as string);
    setMembershipType(product.membership_type);
    setVat(product.vat);
    setPrice(product.price);
    setFormErrors(defaultErrorsObj);
    setEditable(false);
  };

  const submitForm = () => {
    const errors = {
      name: null,
      productCode: null,
      price: null,
      vatRange: null
    } as IErrors;

    let hasErrors = false;
    if (!name || name.length < 2) {
      hasErrors = true;
      errors.name = ErrorTexts.name;
    }
    if (!productCode) {
      hasErrors = true;
      errors.productCode = ErrorTexts.productCode;
    }
    if (!price || price < 0) {
      hasErrors = true;
      errors.price = ErrorTexts.price;
    }
    if (vat && (vat < 0 || vat > 100)) {
      hasErrors = true;
      errors.vatRange = ErrorTexts.vatRange;
    }
    if (
      !hasErrors &&
      name.trim().toUpperCase() !== product.name.toUpperCase() &&
      sameProductNameExists(name.trim(), product.id)
    ) {
      hasErrors = true;
      errors.name = ErrorTexts.nameExists;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      submitProduct();
    }
  };

  const submitProduct = () => {
    const { email_subject, email_banner, email_body } = product;
    updateProduct({
      variables: {
        id: parseInt(product.id),
        input: {
          name: name.trim(),
          product_code_id: parseInt(productCode?.id as string),
          price,
          vat,
          membership_type_id: membershipType ? Number(membershipType.id) : null,
          bottom_notes: bottomNotes ? bottomNotes.trim() : null,
          email_subject,
          email_banner,
          email_body
        }
      }
    }).catch((e) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message.slice(15),
          autoHide: 6000
        }
      })
    );
  };

  return (
    <Box sx={{ pt: '150px', px: '12%', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
        <EditButton
          id="contact-general-edit-button"
          onClick={handleEditClicked}
          disabled={!canEdit || editable}
          editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: '10%'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '45px'
          }}
        >
          <InputComponent
            id="product-general-name"
            label="Name"
            value={name}
            onChange={handleNameChange}
            disabled={!editable || loading}
            errorMsg={formErrors.name}
            additionalStyle={{ gridColumn: 'span 2' }}
          />
          <AutocompleteComponent
            id="product-general-product-code"
            label="Product Code"
            options={productCodesOptions}
            getOptionLabel={(option: IProductCode) => option.name}
            disabled={!editable || loading}
            onChange={handleProductCodeChanged}
            values={productCode}
            errorMsg={formErrors.productCode}
          />
          <AutocompleteComponent
            id="product-general-membership-type"
            label="Membership type"
            options={membershipTypes.data?.result.membership_types || []}
            getOptionLabel={(option: IMembershipType) => option?.name}
            disabled={!editable || productCode?.name !== RB_NAME || loading}
            values={membershipType}
            onChange={(_, newValue) => setMembershipType(newValue)}
          />
          <InputComponent
            id="product-general-price"
            label="Price"
            type="number"
            value={price}
            onChange={handlePriceChange}
            disabled={!editable || loading}
            errorMsg={formErrors.price}
          />
          <InputComponent
            id="product-general-vat"
            label="VAT %"
            type="number"
            value={vat || ''}
            onChange={handleVatChange}
            disabled={!editable || loading}
            errorMsg={formErrors.vatRange}
            required={false}
          />
        </Box>
        <InputComponent
          id="product-general-bottom-notes"
          label="Bottom notes"
          value={bottomNotes || ''}
          onChange={(e) => setBottomNotes(e.currentTarget.value)}
          multiline
          rows={9}
          disabled={!editable || loading}
        />
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '60px' }}>
          <CancelButton
            id="product-general-cancel-button"
            disabled={loading}
            onClick={handleCancel}
            text="Cancel"
          />
          <SaveButton
            id="product-general-save-button"
            disabled={loading}
            onClick={submitForm}
            text="Save"
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductGeneralTab;
